import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, Send, X } from 'lucide-react';
import { ROLE_LABELS, type ClubDetail, type ClubPlayer, type ClubTeam, type ClubTeamDetail, type SendPreview, type TeamStatus } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatLongDay, formatShortDay, today } from '../../lib/dates';
import { Alert, Button, Dialog, Field, LoadingBlock, TextArea } from '../ui';

export function useClub(clubId: string) {
  return useQuery({ queryKey: ['club', clubId], queryFn: () => api.get<ClubDetail>(`/clubs/${clubId}`) });
}

export function TeamStatusBadge({ team }: { team: Pick<ClubTeam, 'status' | 'goLiveOn'> }) {
  const s: Record<TeamStatus, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'bg-sunken text-muted' },
    scheduled: { label: team.goLiveOn ? `Goes live ${formatShortDay(team.goLiveOn)}` : 'Scheduled', cls: 'bg-warn-soft text-warn' },
    live: { label: 'Live', cls: 'bg-ok-soft text-ok' },
  };
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-medium whitespace-nowrap ${s[team.status].cls}`}>{s[team.status].label}</span>;
}

// ---------- A player's emails ----------

interface Contact {
  key: number;
  email: string;
  kind: 'player' | 'parent';
}
let nextKey = 1;

function ContactsInput({ contacts, onChange, errors }: { contacts: Contact[]; onChange: (c: Contact[]) => void; errors: Record<string, string> }) {
  let filled = -1;
  return (
    <fieldset>
      <legend className="text-[14px] font-medium">Emails (optional)</legend>
      <p className="mt-0.5 text-[13px] text-muted">The player’s own, or a parent’s. They’re invited when a team they’re on goes live.</p>
      <div className="mt-2 space-y-2">
        {contacts.map((c, i) => {
          if (c.email.trim()) filled++;
          const err = c.email.trim() ? (errors[`contacts.${filled}.email`] ?? errors[`contacts.${filled}.kind`]) : undefined;
          return (
            <div key={c.key}>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  className={`field-input min-w-0 flex-1 py-1.5 text-[14px] ${err ? 'border-danger' : ''}`}
                  value={c.email}
                  onChange={(e) => onChange(contacts.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                  placeholder="name@example.com"
                  aria-label={`Email ${i + 1}`}
                  autoComplete="off"
                />
                <select className="field-input w-auto py-1.5 text-[14px]" value={c.kind} onChange={(e) => onChange(contacts.map((x, j) => (j === i ? { ...x, kind: e.target.value as Contact['kind'] } : x)))} aria-label={`Email ${i + 1} belongs to`}>
                  <option value="parent">Parent</option>
                  <option value="player">Player</option>
                </select>
                <button type="button" className="btn-ghost p-1.5" onClick={() => onChange(contacts.filter((_, j) => j !== i))} aria-label={`Remove email ${i + 1}`}>
                  <X className="size-4" />
                </button>
              </div>
              {err && <p className="mt-1 text-[13px] text-danger">{err}</p>}
            </div>
          );
        })}
      </div>
      {contacts.length < 4 && (
        <button type="button" className="mt-2 inline-flex items-center gap-1 text-[14px] font-medium text-action hover:underline" onClick={() => onChange([...contacts, { key: nextKey++, email: '', kind: 'parent' }])}>
          <Plus className="size-4" aria-hidden /> Add an email
        </button>
      )}
      {errors.contacts && <p className="mt-1 text-[13px] text-danger">{errors.contacts}</p>}
    </fieldset>
  );
}

/** Add or edit someone on the club's player list. */
export function ClubPlayerDialog({ clubId, player, onClose, onSaved }: { clubId: string; player: ClubPlayer | null; onClose: () => void; onSaved?: (p: ClubPlayer) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(player?.name ?? '');
  const [notes, setNotes] = useState(player?.notes ?? '');
  const [contacts, setContacts] = useState<Contact[]>(() => (player?.contacts.length ? player.contacts.map((c) => ({ key: nextKey++, ...c })) : [{ key: nextKey++, email: '', kind: 'parent' }]));
  const save = useMutation({
    mutationFn: () => {
      const body = { name, notes, contacts: contacts.filter((c) => c.email.trim()).map((c) => ({ email: c.email.trim(), kind: c.kind })) };
      return player ? api.patch<ClubPlayer>(`/clubs/${clubId}/players/${player.id}`, body) : api.post<ClubPlayer>(`/clubs/${clubId}/players`, body);
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['club-players', clubId] });
      void qc.invalidateQueries({ queryKey: ['club-team'] });
      void qc.invalidateQueries({ queryKey: ['club', clubId] });
      onSaved?.(p);
      onClose();
    },
  });
  const [sure, setSure] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.del(`/clubs/${clubId}/players/${player!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['club-players', clubId] });
      void qc.invalidateQueries({ queryKey: ['club', clubId] });
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  const onLive = player?.teams.length ?? 0;
  return (
    <Dialog open onClose={onClose} title={player ? `Edit ${player.name}` : 'Add a player'}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} error={f.name} maxLength={80} autoFocus />
        <ContactsInput contacts={contacts} onChange={setContacts} errors={f} />
        <TextArea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} error={f.notes} maxLength={1000} rows={2} hint="Only club admins and registrars see these." />
        {onLive > 0 && <p className="text-[13px] text-muted">Changes to the name and emails also go to {onLive === 1 ? 'their team' : `their ${onLive} teams`}. On a live team, a new email is invited straight away.</p>}
        {sure && (
          <Alert tone="warn">
            Remove {player!.name} from the club’s list? They stay on any team they’re on.{' '}
            <button type="button" className="font-semibold text-danger underline" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Remove
            </button>
          </Alert>
        )}
        {remove.isError && <Alert>{errorMessage(remove.error)}</Alert>}
        <div className="flex flex-wrap justify-end gap-2">
          {player && !sure && (
            <Button type="button" variant="ghost" className="btn-ghost mr-auto text-danger" onClick={() => setSure(true)}>
              Remove from list
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {player ? 'Save player' : 'Add player'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Going live ----------

const ROLE_WORD: Record<SendPreview['emails'][number]['role'], string> = { head_coach: ROLE_LABELS.head_coach, assistant_coach: ROLE_LABELS.assistant_coach, viewer: 'Read only', player: 'Player', parent: 'Parent' };

/**
 * The warning before invites go out: exactly who'll be emailed, then send now, on a date,
 * or keep it as a draft.
 */
export function GoLiveDialog({ clubId, team, onClose }: { clubId: string; team: ClubTeamDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ['send-preview', team.id], queryFn: () => api.get<SendPreview>(`/clubs/${clubId}/teams/${team.id}/send-preview`) });
  const [when, setWhen] = useState<'now' | 'date' | 'draft'>(team.status === 'scheduled' ? 'date' : 'now');
  const tomorrow = (() => {
    const d = new Date(`${today()}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [date, setDate] = useState(team.goLiveOn ?? tomorrow);
  const go = useMutation({
    mutationFn: () => api.post<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/go-live`, when === 'date' ? { when, date } : { when }),
    onSuccess: (t) => {
      qc.setQueryData(['club-team', clubId, team.id], t);
      void qc.invalidateQueries({ queryKey: ['club-teams', clubId] });
      onClose();
    },
  });
  const p = preview.data;
  const total = p ? p.emails.length : 0;
  const counts = p
    ? [p.coaches && `${p.coaches} ${p.coaches === 1 ? 'coach' : 'coaches'}`, p.players && `${p.players} ${p.players === 1 ? 'player' : 'players'}`, p.parents && `${p.parents} ${p.parents === 1 ? 'parent' : 'parents'}`].filter(Boolean).join(', ')
    : '';
  const label = when === 'now' ? (total ? `Send ${total} ${total === 1 ? 'email' : 'emails'} now` : 'Make the team live') : when === 'date' ? `Send on ${formatShortDay(date)}` : 'Keep as a draft';
  return (
    <Dialog open onClose={onClose} title={`Send invites for ${team.name}?`}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); go.mutate(); }} className="space-y-4" noValidate>
        {preview.isLoading ? (
          <LoadingBlock />
        ) : preview.isError ? (
          <Alert>{errorMessage(preview.error)}</Alert>
        ) : total ? (
          <div className="rounded-lg border border-warn/40 bg-warn-soft p-3.5">
            <p className="flex items-start gap-2 font-semibold">
              <Mail className="mt-0.5 size-4 shrink-0" aria-hidden />
              This emails {counts}.
            </p>
            <p className="mt-1 text-[14px]">
              Each gets an invite to join {team.name} on Hoopify{p!.existing ? `. ${p!.existing === 1 ? '1 of them already has' : `${p!.existing} of them already have`} an account and just needs to accept` : ''}. Emails can’t be taken back once they’ve gone.
            </p>
            <details className="mt-2 text-[14px]">
              <summary className="cursor-pointer font-medium">Who exactly</summary>
              <ul className="mt-1.5 space-y-0.5">
                {p!.emails.map((e) => (
                  <li key={`${e.role}:${e.email}`} className="flex justify-between gap-3">
                    <span className="truncate">{e.email}</span>
                    <span className="shrink-0 text-muted">{ROLE_WORD[e.role]}</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        ) : (
          <Alert tone="info">Nobody is waiting for an invite yet. Add coaches and players first, or make the team live now and they’ll be invited as you add them.</Alert>
        )}
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-[14px] font-medium">When</legend>
          {(
            [
              ['now', 'Send now', 'Everyone above is emailed straight away.'],
              ['date', 'Send on a date', 'For next season: set it up now, and the invites go out early that day.'],
              ['draft', 'Keep as a draft', 'Nothing is sent. Come back when you’re ready.'],
            ] as const
          ).map(([v, title, sub]) => (
            <label key={v} className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 ${when === v ? 'border-action bg-action-soft' : 'border-line hover:bg-sunken'}`}>
              <input type="radio" name="when" className="mt-1 accent-[var(--action)]" checked={when === v} onChange={() => setWhen(v)} />
              <span>
                <span className="block font-medium">{title}</span>
                <span className="block text-[13px] text-muted">{sub}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {when === 'date' && <Field label="Send on" type="date" value={date} min={tomorrow} onChange={(e) => setDate(e.target.value)} hint={date ? `Invites go out on ${formatLongDay(date)}, in the club’s time zone.` : undefined} />}
        {go.isError && <Alert>{errorMessage(go.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={go.isPending} disabled={preview.isLoading || (when === 'date' && !date)}>
            {when === 'now' && <Send className="size-4" aria-hidden />}
            {label}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
